import React from "react";
import { useNavigate } from "react-router-dom";
import { INITIAL_NEWS } from "../../mock/mockData";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Switch } from "../../components/ui/switch";
import { Plus, Trash2, Edit3, Eye, Save, Edit } from "lucide-react";
import { apiClient, setAdminToken } from "../../lib/apiClient";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { AboutUsSectionManager } from "../../components/admin/AboutUsSectionManager";
import { HtmlContentEditor } from "../../components/admin/HtmlContentEditor";

const STORAGE_KEY = "sla_admin_news_mock";
const isValidMongoId = (id) => /^[0-9a-fA-F]{24}$/.test(id || "");

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = React.useState("news"); // "news" or "dashboard"
  const [news, setNews] = React.useState(INITIAL_NEWS);
  const [loading, setLoading] = React.useState(true);
  const [apiError, setApiError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [editing, setEditing] = React.useState(null); // { ...full news object... }
  const [editingLang, setEditingLang] = React.useState("en");

  React.useEffect(() => {
    const token = window.localStorage.getItem("sla_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get("/admin/news");
        const items = response.data?.items;
        if (Array.isArray(items) && items.length) {
          const normalised = items.map((item) => ({
            id: item.id,
            imageUrl: item.image_url ?? null,
            publishedAt: item.published_at?.slice(0, 10) ?? "",
            tags: item.tags || [],
            isPublished: item.is_published,
            title: { en: item.title_en, ua: item.title_uk },
            summary: { en: item.summary_en, ua: item.summary_uk },
            slugEn: item.slug_en || "",
            slugUa: item.slug_uk || "",
          }));
          setNews(normalised);
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalised));
        } else {
          const stored = window.localStorage.getItem(STORAGE_KEY);
          if (stored) {
            setNews(JSON.parse(stored));
          } else {
            setNews(INITIAL_NEWS);
          }
        }
        setApiError("");
      } catch (err) {
        console.error("Failed to load news from API, using local mocks", err);
        setApiError("API unreachable, using local demo data only.");
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored) {
          setNews(JSON.parse(stored));
        } else {
          setNews(INITIAL_NEWS);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [navigate]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(news));
  }, [news]);

  const handleLogout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  const handleOpenEditor = async (id) => {
    if (!isValidMongoId(id)) {
      // For legacy mock items open lightweight editor (without fetching)
      const item = news.find((n) => n.id === id);
      if (!item) return;
      setEditing({
        id: item.id,
        imageUrl: item.imageUrl,
        publishedAt: item.publishedAt,
        tags: item.tags,
        isPublished: item.isPublished,
        title: { ...item.title },
        summary: { ...item.summary },
        content: { en: "", ua: "" },
        slugEn: item.slugEn || "",
        slugUa: item.slugUa || "",
        seoTitleEn: "",
        seoTitleUa: "",
        seoDescEn: "",
        seoDescUa: "",
      });
      setEditingLang("en");
      return;
    }

    try {
      const response = await apiClient.get(`/admin/news/${id}`);
      const item = response.data;
      setEditing({
        id: item.id,
        imageUrl: item.image_url ?? null,
        publishedAt: item.published_at?.slice(0, 10) ?? "",
        tags: item.tags || [],
        isPublished: item.is_published,
        title: { en: item.title_en, ua: item.title_uk },
        summary: { en: item.summary_en, ua: item.summary_uk },
        content: { en: item.content_en || "", ua: item.content_uk || "" },
        slugEn: item.slug_en || "",
        slugUa: item.slug_uk || "",
        seoTitleEn: item.seo_title_en || "",
        seoTitleUa: item.seo_title_uk || "",
        seoDescEn: item.seo_description_en || "",
        seoDescUa: item.seo_description_uk || "",
      });
      setEditingLang("en");
    } catch (err) {
      console.error("Failed to load news detail", err);
      setApiError("Failed to load item for editing.");
    }
  };

  const handleCreateNews = async () => {
    const draft = {
      id: `temp-${Date.now()}`,
      imageUrl: null,
      publishedAt: new Date().toISOString().slice(0, 10),
      tags: [],
      isPublished: false,
      title: { en: "Untitled news", ua: "Новина без назви" },
      summary: {
        en: "Short summary in English.",
        ua: "Короткий опис українською.",
      },
      slugEn: "",
      slugUa: "",
    };
    setNews((prev) => [draft, ...prev]);
    setEditing({
      ...draft,
      content: { en: "", ua: "" },
      seoTitleEn: "",
      seoTitleUa: "",
      seoDescEn: "",
      seoDescUa: "",
    });
    setEditingLang("en");

    try {
      const response = await apiClient.post("/admin/news", {
        title_en: draft.title.en,
        title_uk: draft.title.ua,
        summary_en: draft.summary.en,
        summary_uk: draft.summary.ua,
        content_en: "",
        content_uk: "",
        tags: draft.tags,
        image_url: draft.imageUrl,
        is_published: draft.isPublished,
        published_at: null,
        slug_en: draft.slugEn,
        slug_uk: draft.slugUa,
      });
      const created = response.data;
      setNews((prev) =>
        prev.map((n) =>
          n.id === draft.id
            ? {
                id: created.id,
                imageUrl: created.image_url ?? null,
                publishedAt: created.published_at?.slice(0, 10) ?? draft.publishedAt,
                tags: created.tags || [],
                isPublished: created.is_published,
                title: { en: created.title_en, ua: created.title_uk },
                summary: { en: created.summary_en, ua: created.summary_uk },
                slugEn: created.slug_en || "",
                slugUa: created.slug_uk || "",
              }
            : n
        )
      );
      setEditing((prev) =>
        prev
          ? {
              ...prev,
              id: created.id,
              slugEn: created.slug_en || prev.slugEn,
              slugUa: created.slug_uk || prev.slugUa,
            }
          : prev
      );
    } catch (err) {
      console.error("Failed to create news on server", err);
      setApiError("Cannot create news on server – changes kept only locally.");
    }
  };

  const handleDeleteNews = async (id) => {
    const previous = news;
    setNews((prev) => prev.filter((item) => item.id !== id));

    if (!isValidMongoId(id)) {
      return;
    }

    try {
      await apiClient.delete(`/admin/news/${id}`);
    } catch (err) {
      console.error("Failed to delete on server, restoring previous state", err);
      setNews(previous);
    }
  };

  const handleFieldChange = (field, value, lang) => {
    setEditing((prev) => {
      if (!prev) return prev;
      if (field === "title" || field === "summary" || field === "content") {
        return { ...prev, [field]: { ...prev[field], [lang]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSaveEditing = async () => {
    if (!editing) return;

    // Convert date string (YYYY-MM-DD) to ISO datetime string
    const publishedAtISO = editing.publishedAt 
      ? new Date(editing.publishedAt + "T12:00:00").toISOString()
      : null;

    const payload = {
      title_en: editing.title.en,
      title_uk: editing.title.ua,
      summary_en: editing.summary.en,
      summary_uk: editing.summary.ua,
      content_en: editing.content.en,
      content_uk: editing.content.ua,
      tags: editing.tags,
      image_url: editing.imageUrl,
      is_published: editing.isPublished,
      published_at: publishedAtISO,
      slug_en: editing.slugEn,
      slug_uk: editing.slugUa,
      seo_title_en: editing.seoTitleEn || null,
      seo_title_uk: editing.seoTitleUa || null,
      seo_description_en: editing.seoDescEn || null,
      seo_description_uk: editing.seoDescUa || null,
    };

    try {
      if (!isValidMongoId(editing.id)) {
        const response = await apiClient.post("/admin/news", payload);
        const created = response.data;
        setNews((prev) =>
          prev.map((n) =>
            n.id === editing.id
              ? {
                  id: created.id,
                  imageUrl: created.image_url ?? null,
                  publishedAt: created.published_at?.slice(0, 10) ?? editing.publishedAt,
                  tags: created.tags || [],
                  isPublished: created.is_published,
                  title: { en: created.title_en, ua: created.title_uk },
                  summary: { en: created.summary_en, ua: created.summary_uk },
                  slugEn: created.slug_en || "",
                  slugUa: created.slug_uk || "",
                }
              : n
          )
        );
        setEditing((prev) => (prev ? { ...prev, id: created.id } : prev));
      } else {
        await apiClient.put(`/admin/news/${editing.id}`, payload);
        setNews((prev) =>
          prev.map((n) =>
            n.id === editing.id
              ? {
                  ...n,
                  imageUrl: editing.imageUrl,
                  publishedAt: editing.publishedAt,
                  tags: editing.tags,
                  isPublished: editing.isPublished,
                  title: { ...editing.title },
                  summary: { ...editing.summary },
                  slugEn: editing.slugEn,
                  slugUa: editing.slugUa,
                }
              : n
          )
        );
      }
      setApiError("");
    } catch (err) {
      console.error("Failed to save news on server", err);
      setApiError("Failed to sync some changes with server.");
    }
  };

  const filteredNews = news.filter((item) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      item.title.en.toLowerCase().includes(q) ||
      item.title.ua.toLowerCase().includes(q) ||
      (item.slugEn || "").toLowerCase().includes(q) ||
      (item.slugUa || "").toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout 
      activeSection={activeSection} 
      onNavigate={(section) => {
        setActiveSection(section);
        setEditing(null); // Close editor when switching sections
      }}
      onLogout={handleLogout}
    >
      {activeSection === "dashboard" ? (
        <DashboardSection />
      ) : activeSection === "who-we-are" ? (
        <WhoWeAreSection />
      ) : activeSection === "what-we-do" ? (
        <WhatWeDoSlidesSection />
      ) : activeSection === "about" ? (
        <AboutUsSection />
      ) : activeSection === "contacts" ? (
        <ContactsSection />
      ) : !editing ? (
        <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl shadow-slate-950/80">
          <CardContent className="space-y-4 p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="font-display text-lg font-semibold tracking-tight text-slate-50">Новини</h1>
                <p className="text-sm text-slate-400">
                  Керуйте новинами сайту SLA: створення, публікація та редагування.
                </p>
                {apiError && <p className="mt-1 text-xs text-amber-400">{apiError}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  placeholder="Пошук..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-40 border-slate-700 bg-slate-900 text-sm text-slate-100 placeholder-slate-500"
                />
                <Button
                  size="sm"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-950 hover:bg-amber-400"
                  onClick={handleCreateNews}
                >
                  <Plus className="h-3.5 w-3.5" /> Створити
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/80">
              <Table>
                <TableHeader className="bg-slate-900/80">
                  <TableRow className="border-slate-800">
                    <TableHead className="w-14 text-slate-400">ID</TableHead>
                    <TableHead className="w-20 text-slate-400">Зображення</TableHead>
                    <TableHead className="text-slate-400">Заголовок</TableHead>
                    <TableHead className="w-32 text-center text-slate-400">Активно</TableHead>
                    <TableHead className="w-32 text-slate-400">Дата публікації</TableHead>
                    <TableHead className="w-24 text-right text-slate-400">Дії</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-6 text-center text-sm text-slate-400">
                        Завантаження даних...
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && filteredNews.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-6 text-center text-sm text-slate-400">
                        Новин поки немає.
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    filteredNews.map((item) => (
                      <TableRow key={item.id} className="border-slate-800/80 hover:bg-slate-900/70">
                        <TableCell className="text-xs text-slate-400">{item.id.slice(-4)}</TableCell>
                        <TableCell>
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.title.en}
                              className="h-10 w-14 rounded-md object-cover"
                            />
                          ) : (
                            <div className="flex h-10 w-14 items-center justify-center rounded-md bg-slate-900 text-[10px] text-slate-500">
                              N/A
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs text-sm font-medium text-slate-100">
                            {item.title.en || item.title.ua}
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            {item.slugEn || item.slugUa || "(no slug)"}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={item.isPublished}
                            onCheckedChange={async (checked) => {
                              // Оптимістично оновлюємо UI
                              setNews((prev) =>
                                prev.map((n) =>
                                  n.id === item.id ? { ...n, isPublished: checked } : n
                                )
                              );
                              
                              // Зберігаємо зміну на сервері
                              if (isValidMongoId(item.id)) {
                                try {
                                  await apiClient.put(`/admin/news/${item.id}`, {
                                    title_en: item.title.en,
                                    title_uk: item.title.ua,
                                    summary_en: item.summary.en,
                                    summary_uk: item.summary.ua,
                                    is_published: checked,
                                    tags: item.tags,
                                    image_url: item.imageUrl,
                                    slug_en: item.slugEn,
                                    slug_uk: item.slugUa,
                                  });
                                } catch (err) {
                                  console.error("Failed to update news status", err);
                                  // Відкочуємо зміну у разі помилки
                                  setNews((prev) =>
                                    prev.map((n) =>
                                      n.id === item.id ? { ...n, isPublished: !checked } : n
                                    )
                                  );
                                }
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-sm text-slate-300">
                          {item.publishedAt || "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 rounded-full border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                              onClick={() => handleOpenEditor(item.id)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 rounded-full border-red-500/60 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleDeleteNews(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <NewsEditor
          editing={editing}
          setEditing={setEditing}
          editingLang={editingLang}
          setEditingLang={setEditingLang}
          onSave={handleSaveEditing}
          onBack={() => setEditing(null)}
        />
      )}
    </AdminLayout>
  );
}

function NewsEditor({
  editing,
  setEditing,
  editingLang,
  setEditingLang,
  onSave,
  onBack,
}) {
  const fileInputRef = React.useRef(null);

  const langLabel = editingLang === "en" ? "EN" : "UA";

  return (
    <Card className="border-slate-800/80 bg-slate-950/95 shadow-xl shadow-slate-950/80">
      <CardContent className="space-y-6 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-lg font-semibold tracking-tight text-slate-50">
              Змінити Новину
            </h1>
            <p className="text-sm text-slate-400">
              ID: <span className="font-mono text-slate-200">{editing.id}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-slate-700 bg-slate-900 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-200 hover:bg-slate-800"
              onClick={onBack}
            >
              Назад до списку
            </Button>
            <Button
              size="sm"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-950 hover:bg-amber-400"
              onClick={onSave}
            >
              <Save className="h-3.5 w-3.5" /> Зберегти
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr),minmax(0,1fr)]">
          {/* Left: multilingual content */}
          <div className="space-y-4">
            <Tabs value={editingLang} onValueChange={setEditingLang} className="space-y-4">
              <TabsList className="bg-slate-900/80">
                <TabsTrigger value="en">EN</TabsTrigger>
                <TabsTrigger value="ua">UA</TabsTrigger>
              </TabsList>

              <TabsContent value="en" className="space-y-4">
                <Field label="Заголовок (EN)">
                  <Input
                    value={editing.title.en}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        title: { ...prev.title, en: e.target.value },
                      }))
                    }
                    className="bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                </Field>
                <Field label="Короткий опис (EN)">
                  <Textarea
                    value={editing.summary.en}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        summary: { ...prev.summary, en: e.target.value },
                      }))
                    }
                    className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                </Field>
                <Field label="Контент (EN)">
                  <HtmlContentEditor
                    value={editing.content.en}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        content: { ...prev.content, en: e.target.value },
                      }))
                    }
                  />
                </Field>
              </TabsContent>

              <TabsContent value="ua" className="space-y-4">
                <Field label="Заголовок (UA)">
                  <Input
                    value={editing.title.ua}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        title: { ...prev.title, ua: e.target.value },
                      }))
                    }
                    className="bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                </Field>
                <Field label="Короткий опис (UA)">
                  <Textarea
                    value={editing.summary.ua}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        summary: { ...prev.summary, ua: e.target.value },
                      }))
                    }
                    className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                </Field>
                <Field label="Контент (UA)">
                  <HtmlContentEditor
                    value={editing.content.ua}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        content: { ...prev.content, ua: e.target.value },
                      }))
                    }
                  />
                </Field>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right: meta panel */}
          <div className="space-y-4">
            <Card className="border-slate-800 bg-slate-950/80">
              <CardContent className="space-y-3 p-4">
                <Field label="Опубліковано">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">
                      {editing.isPublished ? "Опубліковано" : "Чернетка"}
                    </span>
                    <Switch
                      checked={editing.isPublished}
                      onCheckedChange={(checked) =>
                        setEditing((prev) => ({ ...prev, isPublished: checked }))
                      }
                    />
                  </div>
                </Field>
                <Field label="Дата публікації">
                  <Input
                    type="date"
                    value={editing.publishedAt || ""}
                    onChange={(e) => setEditing((prev) => ({ ...prev, publishedAt: e.target.value }))}
                    className="bg-slate-950/60 text-slate-50 border-slate-700 [&::-webkit-calendar-picker-indicator]:invert"
                  />
                </Field>
                <Field label="Slug (EN)">
                  <Input
                    value={editing.slugEn || ""}
                    onChange={(e) => setEditing((prev) => ({ ...prev, slugEn: e.target.value }))}
                    className="bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                </Field>
                <Field label="Slug (UA)">
                  <Input
                    value={editing.slugUa || ""}
                    onChange={(e) => setEditing((prev) => ({ ...prev, slugUa: e.target.value }))}
                    className="bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                </Field>
                <Field label="Зображення новини">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="inline-flex cursor-pointer items-center rounded-full border border-slate-700 bg-slate-900 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-100 hover:bg-slate-800">
                        <span>Завантажити фото</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append("file", file);
                            try {
                              const response = await apiClient.post("/admin/upload-image", formData);
                              const url = response.data?.url;
                              if (url) {
                                setEditing((prev) => (prev ? { ...prev, imageUrl: url } : prev));
                              }
                            } catch (err) {
                              console.error("Failed to upload image", err);
                            } finally {
                              e.target.value = "";
                            }
                          }}
                        />
                      </label>
                      {editing.imageUrl && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full border-red-500/70 text-red-400 hover:bg-red-500/10"
                          onClick={async () => {
                            try {
                              await apiClient.delete("/admin/delete-image", {
                                params: { image_path: editing.imageUrl },
                              });
                            } catch (err) {
                              console.error("Failed to delete image from server", err);
                            } finally {
                              setEditing((prev) => (prev ? { ...prev, imageUrl: null } : prev));
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {editing.imageUrl && (
                      <img
                        src={editing.imageUrl}
                        alt={editing.title[editingLang]}
                        className="mt-1 h-28 w-full rounded-md object-cover"
                      />
                    )}
                  </div>
                </Field>
                <Field label="Теги (через кому)">
                  <Input
                    value={editing.tags.join(", ")}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        tags: e.target.value
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean),
                      }))
                    }
                    className="bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-950/80">
              <CardContent className="space-y-3 p-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  SEO
                </div>
                <p className="text-xs text-slate-500">
                  Мета-дані для пошукових систем (необов'язково)
                </p>
                <Field label="[EN] SEO заголовок">
                  <Input
                    value={editing.seoTitleEn}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        seoTitleEn: e.target.value,
                      }))
                    }
                    className="bg-slate-950/60 text-slate-50 border-slate-700"
                    placeholder="Заголовок для пошукових систем (EN)"
                  />
                </Field>
                <Field label="[UA] SEO заголовок">
                  <Input
                    value={editing.seoTitleUa}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        seoTitleUa: e.target.value,
                      }))
                    }
                    className="bg-slate-950/60 text-slate-50 border-slate-700"
                    placeholder="Заголовок для пошукових систем (UA)"
                  />
                </Field>
                <Field label="[EN] Meta опис">
                  <Textarea
                    value={editing.seoDescEn}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        seoDescEn: e.target.value,
                      }))
                    }
                    className="min-h-[70px] bg-slate-950/60 text-slate-50 border-slate-700"
                    placeholder="Опис для пошукових систем (EN)"
                  />
                </Field>
                <Field label="[UA] Meta опис">
                  <Textarea
                    value={editing.seoDescUa}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        seoDescUa: e.target.value,
                      }))
                    }
                    className="min-h-[70px] bg-slate-950/60 text-slate-50 border-slate-700"
                    placeholder="Опис для пошукових систем (UA)"
                  />
                </Field>
              </CardContent>
            </Card>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardSection({ onLogout }) {
  const [adminEmail, setAdminEmail] = React.useState("");
  const [newEmail, setNewEmail] = React.useState("");
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState({ type: "", text: "" });

  // Fetch current admin info
  React.useEffect(() => {
    const fetchAdminInfo = async () => {
      try {
        const response = await apiClient.get("/auth/me");
        setAdminEmail(response.data.email);
        setNewEmail(response.data.email);
      } catch (err) {
        console.error("Failed to load admin info", err);
      }
    };
    fetchAdminInfo();
  }, []);

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes("@")) {
      setMessage({ type: "error", text: "Введіть коректний email" });
      return;
    }
    
    setLoading(true);
    setMessage({ type: "", text: "" });
    
    try {
      await apiClient.put("/auth/update-email", { 
        new_email: newEmail,
        current_password: currentPassword 
      });
      setAdminEmail(newEmail);
      setCurrentPassword("");
      setMessage({ type: "success", text: "Email успішно оновлено" });
    } catch (err) {
      console.error("Failed to update email", err);
      setMessage({ 
        type: "error", 
        text: err.response?.data?.detail || "Не вдалося оновити email. Перевірте пароль." 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "Заповніть всі поля" });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "Нові паролі не співпадають" });
      return;
    }
    
    if (newPassword.length < 8) {
      setMessage({ type: "error", text: "Новий пароль має містити мінімум 8 символів" });
      return;
    }
    
    setLoading(true);
    setMessage({ type: "", text: "" });
    
    try {
      await apiClient.put("/auth/update-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage({ type: "success", text: "Пароль успішно змінено" });
    } catch (err) {
      console.error("Failed to update password", err);
      setMessage({ 
        type: "error", 
        text: err.response?.data?.detail || "Не вдалося змінити пароль. Перевірте поточний пароль." 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl shadow-slate-950/80">
        <CardContent className="space-y-6 p-4 sm:p-6">
          <div>
            <h1 className="font-display text-lg font-semibold tracking-tight text-slate-50">
              Налаштування облікового запису
            </h1>
            <p className="text-sm text-slate-400">
              Керуйте доступом до адмін-панелі
            </p>
          </div>

          {message.text && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                message.type === "success"
                  ? "border-green-500/30 bg-green-500/10 text-green-400"
                  : "border-red-500/30 bg-red-500/10 text-red-400"
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Email Update Section */}
          <form onSubmit={handleUpdateEmail} className="space-y-4 border-t border-slate-800 pt-6">
            <h2 className="text-base font-semibold text-slate-50">Зміна email</h2>
            <Field label="Поточний email">
              <Input
                type="email"
                value={adminEmail}
                disabled
                className="border-slate-700 bg-slate-900/50 text-slate-400"
              />
            </Field>
            <Field label="Новий email">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="admin@example.com"
                className="border-slate-700 bg-slate-900 text-slate-50 placeholder-slate-500"
              />
            </Field>
            <Field label="Підтвердіть поточний пароль">
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="border-slate-700 bg-slate-900 text-slate-50 placeholder-slate-500"
              />
            </Field>
            <Button
              type="submit"
              disabled={loading || newEmail === adminEmail}
              className="rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400"
            >
              {loading ? "Оновлення..." : "Оновити email"}
            </Button>
          </form>

          {/* Password Update Section */}
          <form onSubmit={handleUpdatePassword} className="space-y-4 border-t border-slate-800 pt-6">
            <h2 className="text-base font-semibold text-slate-50">Зміна паролю</h2>
            <Field label="Поточний пароль">
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="border-slate-700 bg-slate-900 text-slate-50 placeholder-slate-500"
              />
            </Field>
            <Field label="Новий пароль">
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="border-slate-700 bg-slate-900 text-slate-50 placeholder-slate-500"
              />
            </Field>
            <Field label="Підтвердіть новий пароль">
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="border-slate-700 bg-slate-900 text-slate-50 placeholder-slate-500"
              />
            </Field>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400"
            >
              {loading ? "Зміна..." : "Змінити пароль"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function WhoWeAreSection() {
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState({ type: "", text: "" });
  const fileInputRef = React.useRef(null);

  // Load content from API
  React.useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get("/admin/static-content/who-we-are");
        setContent(response.data);
      } catch (err) {
        console.error("Failed to load Who We Are content", err);
        setMessage({ type: "error", text: "Не вдалося завантажити контент" });
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleSave = async () => {
    if (!content) return;

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      await apiClient.put("/admin/static-content/who-we-are", content);
      setMessage({ type: "success", text: "Контент успішно збережено!" });
    } catch (err) {
      console.error("Failed to save content", err);
      setMessage({ type: "error", text: "Не вдалося зберегти контент" });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiClient.post("/admin/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setContent({ ...content, image_url: response.data.url });
      setMessage({ type: "success", text: "Зображення завантажено" });
    } catch (err) {
      console.error("Failed to upload image", err);
      setMessage({ type: "error", text: "Не вдалося завантажити зображення" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-400">Завантаження...</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-400">Контент не знайдено</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl shadow-slate-950/80">
        <CardContent className="space-y-6 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-lg font-semibold tracking-tight text-slate-50">
                Редагування секції "Хто ми"
              </h1>
              <p className="text-sm text-slate-400">
                Управління контентом секції "Who we are" на головній сторінці
              </p>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-amber-500 px-6 text-slate-900 hover:bg-amber-400"
            >
              {saving ? "Збереження..." : "ЗБЕРЕГТИ"}
            </Button>
          </div>

          {message.text && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                message.type === "success"
                  ? "border-green-500/30 bg-green-500/10 text-green-400"
                  : "border-red-500/30 bg-red-500/10 text-red-400"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {/* English Column */}
            <div className="space-y-4 border-r border-slate-800 pr-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">
                Англійська (EN)
              </div>

              <Field label="Заголовок (eyebrow)">
                <Input
                  value={content.title_en}
                  onChange={(e) => setContent({ ...content, title_en: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="Who we are"
                />
              </Field>

              <Field label="Текст (параграф 1)">
                <div className="space-y-2">
                  <Textarea
                    value={content.body_en?.[0] || ""}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        body_en: [e.target.value, content.body_en?.[1] || ""],
                      })
                    }
                    className="min-h-[100px] bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                  <p className="text-xs text-slate-400">
                    💡 Підтримується HTML: <code className="text-amber-400">&lt;b&gt;</code>, <code className="text-amber-400">&lt;i&gt;</code>, <code className="text-amber-400">&lt;u&gt;</code>, <code className="text-amber-400">&lt;a href=""&gt;</code>, <code className="text-amber-400">&lt;br&gt;</code>
                  </p>
                </div>
              </Field>

              <Field label="Текст (параграф 2)">
                <div className="space-y-2">
                  <Textarea
                    value={content.body_en?.[1] || ""}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        body_en: [content.body_en?.[0] || "", e.target.value],
                      })
                    }
                    className="min-h-[100px] bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                  <p className="text-xs text-slate-400">
                    💡 Підтримується HTML: <code className="text-amber-400">&lt;b&gt;</code>, <code className="text-amber-400">&lt;i&gt;</code>, <code className="text-amber-400">&lt;u&gt;</code>, <code className="text-amber-400">&lt;a href=""&gt;</code>, <code className="text-amber-400">&lt;br&gt;</code>
                  </p>
                </div>
              </Field>

              <Field label="Текст кнопки">
                <Input
                  value={content.button_label_en}
                  onChange={(e) => setContent({ ...content, button_label_en: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="More about SLA"
                />
              </Field>

              <Field label="Alt текст зображення">
                <Input
                  value={content.image_alt_en}
                  onChange={(e) => setContent({ ...content, image_alt_en: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="Global alliance meeting"
                />
              </Field>
            </div>

            {/* Ukrainian Column */}
            <div className="space-y-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">
                Українська (UA)
              </div>

              <Field label="Заголовок (eyebrow)">
                <Input
                  value={content.title_uk}
                  onChange={(e) => setContent({ ...content, title_uk: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="Хто ми"
                />
              </Field>

              <Field label="Текст (параграф 1)">
                <div className="space-y-2">
                  <Textarea
                    value={content.body_uk?.[0] || ""}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        body_uk: [e.target.value, content.body_uk?.[1] || ""],
                      })
                    }
                    className="min-h-[100px] bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                  <p className="text-xs text-slate-400">
                    💡 Підтримується HTML: <code className="text-amber-400">&lt;b&gt;</code>, <code className="text-amber-400">&lt;i&gt;</code>, <code className="text-amber-400">&lt;u&gt;</code>, <code className="text-amber-400">&lt;a href=""&gt;</code>, <code className="text-amber-400">&lt;br&gt;</code>
                  </p>
                </div>
              </Field>

              <Field label="Текст (параграф 2)">
                <div className="space-y-2">
                  <Textarea
                    value={content.body_uk?.[1] || ""}
                    onChange={(e) =>
                      setContent({
                        ...content,
                        body_uk: [content.body_uk?.[0] || "", e.target.value],
                      })
                    }
                    className="min-h-[100px] bg-slate-950/60 text-slate-50 border-slate-700"
                  />
                  <p className="text-xs text-slate-400">
                    💡 Підтримується HTML: <code className="text-amber-400">&lt;b&gt;</code>, <code className="text-amber-400">&lt;i&gt;</code>, <code className="text-amber-400">&lt;u&gt;</code>, <code className="text-amber-400">&lt;a href=""&gt;</code>, <code className="text-amber-400">&lt;br&gt;</code>
                  </p>
                </div>
              </Field>

              <Field label="Текст кнопки">
                <Input
                  value={content.button_label_uk}
                  onChange={(e) => setContent({ ...content, button_label_uk: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="Більше про SLA"
                />
              </Field>

              <Field label="Alt текст зображення">
                <Input
                  value={content.image_alt_uk}
                  onChange={(e) => setContent({ ...content, image_alt_uk: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="Зустріч глобального альянсу"
                />
              </Field>
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="border-t border-slate-800 pt-6">
            <Field label="Зображення">
              <div className="space-y-4">
                {content.image_url && (
                  <div className="relative h-48 overflow-hidden rounded-lg border border-slate-700">
                    <img
                      src={content.image_url}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  {content.image_url ? "Змінити зображення" : "Завантажити зображення"}
                </Button>
              </div>
            </Field>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AboutUsSection() {
  const [intro, setIntro] = React.useState(null);
  const [editingIntro, setEditingIntro] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [message, setMessage] = React.useState({ type: "", text: "" });

  React.useEffect(() => {
    fetchIntro();
  }, []);

  const fetchIntro = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/about-intro");
      setIntro(response.data);
    } catch (err) {
      console.error("Failed to load about intro", err);
      setMessage({ type: "error", text: "Не вдалося завантажити вступний текст" });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIntro = async () => {
    if (!intro) return;

    setMessage({ type: "", text: "" });

    try {
      await apiClient.put("/admin/about-intro", intro);
      setMessage({ type: "success", text: "Вступний текст збережено!" });
      setEditingIntro(false);
      fetchIntro();
    } catch (err) {
      console.error("Failed to save intro", err);
      setMessage({ type: "error", text: "Не вдалося зберегти текст" });
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
        <CardContent className="p-6">
          <p className="text-slate-400">Завантаження...</p>
        </CardContent>
      </Card>
    );
  }

  // Helper functions for paragraphs management
  const updateParagraph = (lang, index, value) => {
    const key = lang === "en" ? "paragraphs_en" : "paragraphs_uk";
    const newParagraphs = [...(intro[key] || [])];
    newParagraphs[index] = value;
    setIntro({ ...intro, [key]: newParagraphs });
  };

  const addParagraph = (lang) => {
    const key = lang === "en" ? "paragraphs_en" : "paragraphs_uk";
    setIntro({ ...intro, [key]: [...(intro[key] || []), ""] });
  };

  const removeParagraph = (lang, index) => {
    const key = lang === "en" ? "paragraphs_en" : "paragraphs_uk";
    const newParagraphs = (intro[key] || []).filter((_, i) => i !== index);
    setIntro({ ...intro, [key]: newParagraphs });
  };

  // Show intro editor if editing
  if (editingIntro) {
    return (
      <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
        <CardContent className="space-y-6 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-slate-50">
              Редагувати вступний текст секції
            </h2>
            <div className="flex gap-2">
              <Button
                onClick={() => setEditingIntro(false)}
                variant="outline"
                className="rounded-full border-slate-700 text-slate-300"
              >
                СКАСУВАТИ
              </Button>
              <Button
                onClick={handleSaveIntro}
                className="rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400"
              >
                ЗБЕРЕГТИ
              </Button>
            </div>
          </div>

          {message.text && (
            <div
              className={`rounded-lg p-3 text-sm ${
                message.type === "success"
                  ? "bg-green-500/20 text-green-400"
                  : "bg-red-500/20 text-red-400"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {/* English Column */}
            <div className="space-y-4 border-r border-slate-800 pr-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">
                Англійська (EN)
              </div>

              <Field label="Заголовок секції">
                <Input
                  value={intro?.title_en || ""}
                  onChange={(e) => setIntro({ ...intro, title_en: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="Mission Statement"
                />
              </Field>

              <Field label="Параграфи тексту">
                <div className="space-y-3">
                  {(intro?.paragraphs_en || []).map((paragraph, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">Параграф {index + 1}</span>
                        {(intro?.paragraphs_en || []).length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeParagraph("en", index)}
                            className="text-red-400"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={paragraph}
                        onChange={(e) => updateParagraph("en", index, e.target.value)}
                        className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                        placeholder="Enter paragraph text..."
                      />
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addParagraph("en")}
                    className="w-full border-slate-700 text-slate-300"
                  >
                    + Додати параграф
                  </Button>
                </div>
              </Field>
            </div>

            {/* Ukrainian Column */}
            <div className="space-y-4">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">
                Українська (UA)
              </div>

              <Field label="Заголовок секції">
                <Input
                  value={intro?.title_uk || ""}
                  onChange={(e) => setIntro({ ...intro, title_uk: e.target.value })}
                  className="bg-slate-950/60 text-slate-50 border-slate-700"
                  placeholder="Наша місія"
                />
              </Field>

              <Field label="Параграфи тексту">
                <div className="space-y-3">
                  {(intro?.paragraphs_uk || []).map((paragraph, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">Параграф {index + 1}</span>
                        {(intro?.paragraphs_uk || []).length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeParagraph("uk", index)}
                            className="text-red-400"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={paragraph}
                        onChange={(e) => updateParagraph("uk", index, e.target.value)}
                        className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                        placeholder="Введіть текст параграфу..."
                      />
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addParagraph("uk")}
                    className="w-full border-slate-700 text-slate-300"
                  >
                    + Додати параграф
                  </Button>
                </div>
              </Field>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show main list with intro summary and sections
  return (
    <div className="space-y-6">
      {/* Intro text section */}
      {intro && (
        <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
          <CardContent className="space-y-4 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-base font-semibold text-slate-50">
                  Вступний текст секції
                </h2>
                <p className="text-sm text-slate-400">
                  Текст над вкладками на сторінці "Про нас"
                </p>
              </div>
              <Button
                onClick={() => setEditingIntro(true)}
                variant="outline"
                className="rounded-full border-slate-700 text-slate-300"
              >
                РЕДАГУВАТИ ТЕКСТ
              </Button>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400 mb-2">
                {intro.title_en} / {intro.title_uk}
              </p>
              <div className="space-y-2 text-sm text-slate-300">
                {intro.paragraphs_en?.slice(0, 1).map((p, i) => (
                  <p key={i}>{p.substring(0, 100)}...</p>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sections Manager */}
      <AboutUsSectionManager />
    </div>
  );
}

function WhatWeDoSlidesSection() {
  const [slides, setSlides] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [editingIntro, setEditingIntro] = React.useState(false);
  const [intro, setIntro] = React.useState(null);
  const [message, setMessage] = React.useState({ type: "", text: "" });

  // Load slides and intro from API
  React.useEffect(() => {
    fetchSlides();
    fetchIntro();
  }, []);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/what-we-do-slides");
      setSlides(response.data);
    } catch (err) {
      console.error("Failed to load slides", err);
      setMessage({ type: "error", text: "Не вдалося завантажити слайди" });
    } finally {
      setLoading(false);
    }
  };

  const fetchIntro = async () => {
    try {
      const response = await apiClient.get("/admin/what-we-do-intro");
      setIntro(response.data);
    } catch (err) {
      console.error("Failed to load intro", err);
    }
  };

  const handleCreate = () => {
    setEditing({
      id: null,
      title_en: "",
      title_uk: "",
      intro_en: "",
      intro_uk: "",
      bullets_en: ["", "", ""],
      bullets_uk: ["", "", ""],
      cta_en: "",
      cta_uk: "",
      image_url: "",
      order: slides.length
    });
  };

  const handleEdit = (slide) => {
    setEditing({ ...slide });
  };

  const handleSave = async () => {
    if (!editing) return;

    setMessage({ type: "", text: "" });

    try {
      if (editing.id) {
        // Update existing slide
        await apiClient.put(`/admin/what-we-do-slides/${editing.id}`, editing);
        setMessage({ type: "success", text: "Слайд оновлено!" });
      } else {
        // Create new slide
        await apiClient.post("/admin/what-we-do-slides", editing);
        setMessage({ type: "success", text: "Слайд створено!" });
      }
      setEditing(null);
      fetchSlides();
    } catch (err) {
      console.error("Failed to save slide", err);
      setMessage({ type: "error", text: "Не вдалося зберегти слайд" });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Видалити цей слайд?")) return;

    try {
      await apiClient.delete(`/admin/what-we-do-slides/${id}`);
      setMessage({ type: "success", text: "Слайд видалено" });
      fetchSlides();
    } catch (err) {
      console.error("Failed to delete slide", err);
      setMessage({ type: "error", text: "Не вдалося видалити слайд" });
    }
  };

  const handleSaveIntro = async () => {
    if (!intro) return;

    setMessage({ type: "", text: "" });

    try {
      await apiClient.put("/admin/what-we-do-intro", intro);
      setMessage({ type: "success", text: "Вступний текст збережено!" });
      setEditingIntro(false);
      fetchIntro();
    } catch (err) {
      console.error("Failed to save intro", err);
      setMessage({ type: "error", text: "Не вдалося зберегти текст" });
    }
  };

  if (editingIntro && intro) {
    return (
      <WhatWeDoIntroEditor
        intro={intro}
        setIntro={setIntro}
        onSave={handleSaveIntro}
        onCancel={() => setEditingIntro(false)}
      />
    );
  }

  if (editing) {
    return (
      <WhatWeDoSlideEditor
        slide={editing}
        setSlide={setEditing}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Intro text section */}
      {intro && (
        <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
          <CardContent className="space-y-4 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-base font-semibold text-slate-50">
                  Вступний текст секції
                </h2>
                <p className="text-sm text-slate-400">
                  Текст над каруселлю слайдів
                </p>
              </div>
              <Button
                onClick={() => setEditingIntro(true)}
                variant="outline"
                className="rounded-full border-slate-700 text-slate-300"
              >
                РЕДАГУВАТИ ТЕКСТ
              </Button>
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400 mb-2">
                {intro.title_en} / {intro.title_uk}
              </p>
              <div className="space-y-2 text-sm text-slate-300">
                {intro.paragraphs_en?.slice(0, 1).map((p, i) => (
                  <p key={i}>{p.substring(0, 100)}...</p>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Slides section */}
      <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
        <CardContent className="space-y-6 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-lg font-semibold text-slate-50">
                Слайди каруселі
              </h1>
              <p className="text-sm text-slate-400">
                Управління слайдами каруселі "What we do"
              </p>
            </div>
            <Button
              onClick={handleCreate}
              className="rounded-full bg-amber-500 px-4 text-slate-900 hover:bg-amber-400"
            >
              + СТВОРИТИ СЛАЙД
            </Button>
          </div>

          {message.text && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                message.type === "success"
                  ? "border-green-500/30 bg-green-500/10 text-green-400"
                  : "border-red-500/30 bg-red-500/10 text-red-400"
              }`}
            >
              {message.text}
            </div>
          )}

          {loading ? (
            <p className="text-center text-slate-400">Завантаження...</p>
          ) : slides.length === 0 ? (
            <p className="text-center text-slate-400">Слайдів ще немає</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-slate-400">Порядок</TableHead>
                  <TableHead className="text-slate-400">Зображення</TableHead>
                  <TableHead className="text-slate-400">Заголовок (EN)</TableHead>
                  <TableHead className="text-slate-400">Заголовок (UA)</TableHead>
                  <TableHead className="text-slate-400 text-right">Дії</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slides.map((slide) => (
                  <TableRow key={slide.id}>
                    <TableCell className="text-slate-300">{slide.order}</TableCell>
                    <TableCell>
                      {slide.image_url && (
                        <img
                          src={slide.image_url}
                          alt={slide.title_en}
                          className="h-12 w-16 rounded object-cover"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300">{slide.title_en}</TableCell>
                    <TableCell className="text-slate-300">{slide.title_uk}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(slide)}
                          className="text-slate-400 hover:text-slate-100"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(slide.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WhatWeDoIntroEditor({ intro, setIntro, onSave, onCancel }) {
  const updateParagraph = (lang, index, value) => {
    const key = lang === "en" ? "paragraphs_en" : "paragraphs_uk";
    const newParagraphs = [...intro[key]];
    newParagraphs[index] = value;
    setIntro({ ...intro, [key]: newParagraphs });
  };

  const addParagraph = (lang) => {
    const key = lang === "en" ? "paragraphs_en" : "paragraphs_uk";
    setIntro({ ...intro, [key]: [...intro[key], ""] });
  };

  const removeParagraph = (lang, index) => {
    const key = lang === "en" ? "paragraphs_en" : "paragraphs_uk";
    const newParagraphs = intro[key].filter((_, i) => i !== index);
    setIntro({ ...intro, [key]: newParagraphs });
  };

  return (
    <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
      <CardContent className="space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-50">
            Редагувати вступний текст секції
          </h2>
          <div className="flex gap-2">
            <Button
              onClick={onCancel}
              variant="outline"
              className="rounded-full border-slate-700 text-slate-300"
            >
              СКАСУВАТИ
            </Button>
            <Button
              onClick={onSave}
              className="rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400"
            >
              ЗБЕРЕГТИ
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* English Column */}
          <div className="space-y-4 border-r border-slate-800 pr-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">
              Англійська (EN)
            </div>

            <Field label="Заголовок секції">
              <Input
                value={intro.title_en}
                onChange={(e) => setIntro({ ...intro, title_en: e.target.value })}
                className="bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="What we do"
              />
            </Field>

            <Field label="Параграфи тексту">
              <div className="space-y-3">
                {intro.paragraphs_en.map((paragraph, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Параграф {index + 1}</span>
                      {intro.paragraphs_en.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeParagraph("en", index)}
                          className="text-red-400"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <Textarea
                      value={paragraph}
                      onChange={(e) => updateParagraph("en", index, e.target.value)}
                      className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                      placeholder="Enter paragraph text..."
                    />
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addParagraph("en")}
                  className="w-full border-slate-700 text-slate-300"
                >
                  + Додати параграф
                </Button>
              </div>
            </Field>
          </div>

          {/* Ukrainian Column */}
          <div className="space-y-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">
              Українська (UA)
            </div>

            <Field label="Заголовок секції">
              <Input
                value={intro.title_uk}
                onChange={(e) => setIntro({ ...intro, title_uk: e.target.value })}
                className="bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="Що ми робимо"
              />
            </Field>

            <Field label="Параграфи тексту">
              <div className="space-y-3">
                {intro.paragraphs_uk.map((paragraph, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Параграф {index + 1}</span>
                      {intro.paragraphs_uk.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeParagraph("uk", index)}
                          className="text-red-400"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <Textarea
                      value={paragraph}
                      onChange={(e) => updateParagraph("uk", index, e.target.value)}
                      className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                      placeholder="Введіть текст параграфу..."
                    />
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addParagraph("uk")}
                  className="w-full border-slate-700 text-slate-300"
                >
                  + Додати параграф
                </Button>
              </div>
            </Field>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function WhatWeDoSlideEditor({ slide, setSlide, onSave, onCancel }) {
  const fileInputRef = React.useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiClient.post("/admin/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSlide({ ...slide, image_url: response.data.url });
    } catch (err) {
      console.error("Failed to upload image", err);
    }
  };

  const updateBullet = (lang, index, value) => {
    const key = lang === "en" ? "bullets_en" : "bullets_uk";
    const newBullets = [...slide[key]];
    newBullets[index] = value;
    setSlide({ ...slide, [key]: newBullets });
  };

  const addBullet = (lang) => {
    const key = lang === "en" ? "bullets_en" : "bullets_uk";
    setSlide({ ...slide, [key]: [...slide[key], ""] });
  };

  const removeBullet = (lang, index) => {
    const key = lang === "en" ? "bullets_en" : "bullets_uk";
    const newBullets = slide[key].filter((_, i) => i !== index);
    setSlide({ ...slide, [key]: newBullets });
  };

  return (
    <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
      <CardContent className="space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-50">
            {slide.id ? "Редагувати слайд" : "Створити новий слайд"}
          </h2>
          <div className="flex gap-2">
            <Button
              onClick={onCancel}
              variant="outline"
              className="rounded-full border-slate-700 text-slate-300"
            >
              СКАСУВАТИ
            </Button>
            <Button
              onClick={onSave}
              className="rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400"
            >
              ЗБЕРЕГТИ
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* English Column */}
          <div className="space-y-4 border-r border-slate-800 pr-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">
              Англійська (EN)
            </div>

            <Field label="Заголовок">
              <Input
                value={slide.title_en}
                onChange={(e) => setSlide({ ...slide, title_en: e.target.value })}
                className="bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="Government relations"
              />
            </Field>

            <Field label="Вступний текст">
              <Textarea
                value={slide.intro_en}
                onChange={(e) => setSlide({ ...slide, intro_en: e.target.value })}
                className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="Short introduction..."
              />
            </Field>

            <Field label="Пункти списку">
              <div className="space-y-2">
                {slide.bullets_en.map((bullet, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={bullet}
                      onChange={(e) => updateBullet("en", index, e.target.value)}
                      className="bg-slate-950/60 text-slate-50 border-slate-700"
                      placeholder={`Point ${index + 1}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBullet("en", index)}
                      className="text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addBullet("en")}
                  className="w-full border-slate-700 text-slate-300"
                >
                  + Додати пункт
                </Button>
              </div>
            </Field>

            <Field label="Текст кнопки (опціонально)">
              <Input
                value={slide.cta_en}
                onChange={(e) => setSlide({ ...slide, cta_en: e.target.value })}
                className="bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="Contact us"
              />
            </Field>
          </div>

          {/* Ukrainian Column */}
          <div className="space-y-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">
              Українська (UA)
            </div>

            <Field label="Заголовок">
              <Input
                value={slide.title_uk}
                onChange={(e) => setSlide({ ...slide, title_uk: e.target.value })}
                className="bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="Урядові відносини"
              />
            </Field>

            <Field label="Вступний текст">
              <Textarea
                value={slide.intro_uk}
                onChange={(e) => setSlide({ ...slide, intro_uk: e.target.value })}
                className="min-h-[80px] bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="Короткий вступ..."
              />
            </Field>

            <Field label="Пункти списку">
              <div className="space-y-2">
                {slide.bullets_uk.map((bullet, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={bullet}
                      onChange={(e) => updateBullet("uk", index, e.target.value)}
                      className="bg-slate-950/60 text-slate-50 border-slate-700"
                      placeholder={`Пункт ${index + 1}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBullet("uk", index)}
                      className="text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addBullet("uk")}
                  className="w-full border-slate-700 text-slate-300"
                >
                  + Додати пункт
                </Button>
              </div>
            </Field>

            <Field label="Текст кнопки (опціонально)">
              <Input
                value={slide.cta_uk}
                onChange={(e) => setSlide({ ...slide, cta_uk: e.target.value })}
                className="bg-slate-950/60 text-slate-50 border-slate-700"
                placeholder="Зв'яжіться з нами"
              />
            </Field>
          </div>
        </div>

        {/* Image & Order */}
        <div className="grid gap-6 border-t border-slate-800 pt-6 md:grid-cols-2">
          <Field label="Зображення">
            <div className="space-y-4">
              {slide.image_url && (
                <div className="relative h-32 overflow-hidden rounded-lg border border-slate-700">
                  <img
                    src={slide.image_url}
                    alt="Preview"
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full border-slate-700 text-slate-300"
              >
                {slide.image_url ? "Змінити зображення" : "Завантажити зображення"}
              </Button>
            </div>
          </Field>

          <Field label="Порядок відображення">
            <Input
              type="number"
              value={slide.order}
              onChange={(e) => setSlide({ ...slide, order: parseInt(e.target.value) || 0 })}
              className="bg-slate-950/60 text-slate-50 border-slate-700"
              placeholder="0"
            />
            <p className="text-xs text-slate-500 mt-1">
              Слайди відображаються у порядку зростання
            </p>
          </Field>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5 text-sm">
      <div className="text-xs font-medium text-slate-300">{label}</div>
      {children}
    </div>
  );
}


// ===========================
// Contacts Section
// ===========================
function ContactsSection() {
  const [contacts, setContacts] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState({ washington: false, kyiv: false });
  const [message, setMessage] = React.useState({ text: "", type: "" });
  
  const washingtonFileRef = React.useRef(null);
  const kyivFileRef = React.useRef(null);

  React.useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await apiClient.get("/admin/contacts");
      setContacts(response.data);
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: "", type: "" });
    
    try {
      await apiClient.put("/admin/contacts", contacts);
      setMessage({ text: "Контакти успішно оновлено!", type: "success" });
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    } catch (err) {
      console.error("Failed to save contacts:", err);
      setMessage({ text: "Помилка збереження", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (office, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(prev => ({ ...prev, [office]: true }));
    
    try {
      // Delete old image if exists
      const oldImageUrl = contacts?.[office]?.image_url;
      if (oldImageUrl && oldImageUrl.startsWith('/api/media/')) {
        try {
          const filename = oldImageUrl.split('/').pop();
          await apiClient.delete("/admin/delete-image", {
            params: { image_path: `/uploads/${filename}` }
          });
        } catch (err) {
          console.error("Failed to delete old image:", err);
        }
      }

      // Upload new image
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await apiClient.post("/admin/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setContacts(prev => ({
        ...prev,
        [office]: { ...prev[office], image_url: response.data.url }
      }));
      setMessage({ type: "success", text: "Зображення завантажено" });
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    } catch (err) {
      console.error("Failed to upload image:", err);
      setMessage({ type: "error", text: "Не вдалося завантажити зображення" });
    } finally {
      setUploading(prev => ({ ...prev, [office]: false }));
      // Reset file input
      if (office === 'washington' && washingtonFileRef.current) {
        washingtonFileRef.current.value = '';
      } else if (office === 'kyiv' && kyivFileRef.current) {
        kyivFileRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (office) => {
    const imageUrl = contacts?.[office]?.image_url;
    if (!imageUrl) return;

    try {
      if (imageUrl.startsWith('/api/media/')) {
        const filename = imageUrl.split('/').pop();
        await apiClient.delete("/admin/delete-image", {
          params: { image_path: `/uploads/${filename}` }
        });
      }
      
      setContacts(prev => ({
        ...prev,
        [office]: { ...prev[office], image_url: '' }
      }));
      setMessage({ type: "success", text: "Зображення видалено" });
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    } catch (err) {
      console.error("Failed to delete image:", err);
      setMessage({ type: "error", text: "Не вдалося видалити зображення" });
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-800/80 bg-slate-950/90">
        <CardContent className="p-6 text-center text-slate-400">
          Завантаження...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-800/80 bg-slate-950/90 shadow-xl">
      <CardContent className="space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-lg font-semibold tracking-tight text-slate-50">
              Контакти
            </h1>
            <p className="text-sm text-slate-400">
              Управління контактною інформацією офісів
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-amber-500 px-6 text-slate-900 hover:bg-amber-400"
          >
            {saving ? "Збереження..." : "ЗБЕРЕГТИ"}
          </Button>
        </div>

        {message.text && (
          <div className={`rounded-lg p-3 text-sm ${message.type === "success" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
            {message.text}
          </div>
        )}

        {/* Kyiv Office */}
        <div className="space-y-4 border-t border-slate-800 pt-6">
          <h2 className="text-md font-semibold text-slate-200">Kyiv Office</h2>
          
          <Field label="Назва офісу">
            <Input
              value={contacts?.kyiv?.name || ""}
              onChange={(e) => setContacts({
                ...contacts,
                kyiv: { ...contacts.kyiv, name: e.target.value }
              })}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <Field label="Адреса">
            <Input
              value={contacts?.kyiv?.address || ""}
              onChange={(e) => setContacts({
                ...contacts,
                kyiv: { ...contacts.kyiv, address: e.target.value }
              })}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <Field label="Опис (EN)">
            <Textarea
              value={contacts?.kyiv?.description_en || ""}
              onChange={(e) => setContacts({
                ...contacts,
                kyiv: { ...contacts.kyiv, description_en: e.target.value }
              })}
              rows={3}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <Field label="Опис (UA)">
            <Textarea
              value={contacts?.kyiv?.description_uk || ""}
              onChange={(e) => setContacts({
                ...contacts,
                kyiv: { ...contacts.kyiv, description_uk: e.target.value }
              })}
              rows={3}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Телефон">
              <Input
                value={contacts?.kyiv?.phone || ""}
                onChange={(e) => setContacts({
                  ...contacts,
                  kyiv: { ...contacts.kyiv, phone: e.target.value }
                })}
                className="border-slate-700 bg-slate-900 text-slate-100"
              />
            </Field>

            <Field label="Email">
              <Input
                value={contacts?.kyiv?.email || ""}
                onChange={(e) => setContacts({
                  ...contacts,
                  kyiv: { ...contacts.kyiv, email: e.target.value }
                })}
                className="border-slate-700 bg-slate-900 text-slate-100"
              />
            </Field>
          </div>

          <Field label="Зображення офісу">
            <div className="space-y-3">
              {contacts?.kyiv?.image_url && (
                <div className="relative h-48 overflow-hidden rounded-lg border border-slate-700">
                  <img
                    src={contacts.kyiv.image_url}
                    alt="Kyiv Office"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteImage('kyiv')}
                    className="absolute top-2 right-2 rounded-full bg-red-500/80 p-1.5 text-white hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
              <input
                ref={kyivFileRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('kyiv', e)}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading.kyiv}
                onClick={() => kyivFileRef.current?.click()}
                className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {uploading.kyiv ? "Завантаження..." : contacts?.kyiv?.image_url ? "Змінити зображення" : "Завантажити зображення"}
              </Button>
            </div>
          </Field>
        </div>

        {/* Washington Office */}
        <div className="space-y-4 border-t border-slate-800 pt-6">
          <h2 className="text-md font-semibold text-slate-200">Washington, D.C. Office</h2>
          
          <Field label="Назва офісу">
            <Input
              value={contacts?.washington?.name || ""}
              onChange={(e) => setContacts({
                ...contacts,
                washington: { ...contacts.washington, name: e.target.value }
              })}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <Field label="Адреса">
            <Input
              value={contacts?.washington?.address || ""}
              onChange={(e) => setContacts({
                ...contacts,
                washington: { ...contacts.washington, address: e.target.value }
              })}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <Field label="Опис (EN)">
            <Textarea
              value={contacts?.washington?.description_en || ""}
              onChange={(e) => setContacts({
                ...contacts,
                washington: { ...contacts.washington, description_en: e.target.value }
              })}
              rows={3}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <Field label="Опис (UA)">
            <Textarea
              value={contacts?.washington?.description_uk || ""}
              onChange={(e) => setContacts({
                ...contacts,
                washington: { ...contacts.washington, description_uk: e.target.value }
              })}
              rows={3}
              className="border-slate-700 bg-slate-900 text-slate-100"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Телефон">
              <Input
                value={contacts?.washington?.phone || ""}
                onChange={(e) => setContacts({
                  ...contacts,
                  washington: { ...contacts.washington, phone: e.target.value }
                })}
                className="border-slate-700 bg-slate-900 text-slate-100"
              />
            </Field>

            <Field label="Email">
              <Input
                value={contacts?.washington?.email || ""}
                onChange={(e) => setContacts({
                  ...contacts,
                  washington: { ...contacts.washington, email: e.target.value }
                })}
                className="border-slate-700 bg-slate-900 text-slate-100"
              />
            </Field>
          </div>

          <Field label="Зображення офісу">
            <div className="space-y-3">
              {contacts?.washington?.image_url && (
                <div className="relative h-48 overflow-hidden rounded-lg border border-slate-700">
                  <img
                    src={contacts.washington.image_url}
                    alt="Washington Office"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteImage('washington')}
                    className="absolute top-2 right-2 rounded-full bg-red-500/80 p-1.5 text-white hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
              <input
                ref={washingtonFileRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('washington', e)}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading.washington}
                onClick={() => washingtonFileRef.current?.click()}
                className="rounded-full border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {uploading.washington ? "Завантаження..." : contacts?.washington?.image_url ? "Змінити зображення" : "Завантажити зображення"}
              </Button>
            </div>
          </Field>
        </div>
      </CardContent>
    </Card>
  );
}
